import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ArticleBadge from '../ArticleBadge';

const API = process.env.REACT_APP_BACKEND_URL;
const ROTATE_MS = 3000;

const TopStories = () => {
  const [stories, setStories] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = React.useRef(null);
  const touchEndX = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/articles?limit=8`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data.items) ? data.items : [];
        // Prefer stories with cover images; fall back to all
        const withImg = items.filter((s) => s.cover_image_url);
        const list = (withImg.length >= 6 ? withImg : items).slice(0, 6);
        setStories(list);
      })
      .catch((err) => console.error('Home carousel fetch failed:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (paused || stories.length <= 1) return undefined;
    const t = setInterval(() => {
      setActive((i) => (i + 1) % stories.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, stories.length]);

  const go = useCallback((idx) => {
    if (stories.length === 0) return;
    const n = stories.length;
    setActive(((idx % n) + n) % n);
  }, [stories.length]);

  const onTouchStart = (e) => {
    setPaused(true);
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };
  const onTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const onTouchEnd = () => {
    setPaused(false);
    if (touchStartX.current == null || touchEndX.current == null) return;
    const dx = touchEndX.current - touchStartX.current;
    const SWIPE_THRESHOLD = 40; // px
    if (dx > SWIPE_THRESHOLD) {
      go(active - 1); // swiped right → previous
    } else if (dx < -SWIPE_THRESHOLD) {
      go(active + 1); // swiped left → next
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (stories.length === 0) {
    // Reserve the carousel's footprint so the section below doesn't jump
    // when the article feed resolves (~100-300ms after first paint).
    return (
      <section
        className="relative px-4 pt-1 pb-6"
        aria-hidden="true"
        data-testid="top-stories-placeholder"
      >
        <div className="max-w-6xl mx-auto">
          <div className="h-5 mb-3" />
          <div className="aspect-[16/10] md:aspect-[16/8] rounded-2xl bg-zinc-950/60 border border-red-900/20" />
          <div className="flex items-center justify-center gap-1.5 mt-3 h-1.5" />
        </div>
      </section>
    );
  }

  const current = stories[active];
  const formatPostedAt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const datePart = d.toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      timeZone: 'Australia/Melbourne',
    });
    const timePart = d.toLocaleTimeString('en-AU', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: 'Australia/Melbourne',
    });
    return `${datePart} · ${timePart.toLowerCase()}`;
  };
  const currentPostedAt = formatPostedAt(current.published_at);

  return (
    <section
      className="relative px-4 pt-8 md:pt-10 pb-6"
      data-testid="top-stories"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="max-w-6xl mx-auto">
        {/* Card - flatter, cleaner, no red neon glow */}
        <Link
          key={current.slug}
          to={`/stories/${current.slug}`}
          data-testid={`carousel-card-${active}`}
          className="block relative aspect-[16/10] md:aspect-[16/8] rounded-xl overflow-hidden border border-zinc-800 group animate-card-fade"
        >
          {current.cover_image_url ? (
            <img
              src={current.cover_image_url}
              alt={current.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 to-black"></div>
          )}
          {/* Headline gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>

          <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
            <ArticleBadge article={current} size="md" testId="carousel-article-badge" />
            <h3 className="mt-3 text-white font-black leading-tight text-lg md:text-2xl">
              {current.title}
            </h3>
            {currentPostedAt && (
              <p className="mt-2 text-[11px] font-bold tracking-wider uppercase text-gray-400" data-testid="carousel-posted-at">
                {currentPostedAt}
              </p>
            )}
          </div>
        </Link>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-4" data-testid="carousel-dots">
          {stories.map((s, idx) => (
            <button
              key={s.slug}
              type="button"
              onClick={() => go(idx)}
              data-testid={`carousel-dot-${idx}`}
              aria-label={`Show story ${idx + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx === active ? 'w-8 bg-red-500' : 'w-4 bg-zinc-700 hover:bg-zinc-500'
              }`}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes card-fade {
          from { opacity: 0.3; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-card-fade { animation: card-fade 0.4s ease-out; }
      `}</style>
    </section>
  );
};

export default TopStories;
